import { motion } from 'framer-motion';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Card, CardBody, CardHeader, CardTitle } from '@aurum/ui';

import { CartDrawer } from '../features/cart/CartDrawer';
import { useCartStore } from '../features/cart/cart-store';
import { CustomizerSheet } from '../features/customizer/CustomizerSheet';
import { TeaserIntro } from '../features/intro/TeaserIntro';
import { SommelierBar } from '../features/sommelier/SommelierBar';
import { useMenu } from '../hooks/useMenu';
import type { ApiMenuItem } from '../lib/api';

interface SelectedItem {
  item: ApiMenuItem;
  categorySlug: string;
}

function formatPrice(value: number, currency: string): string {
  if (currency === 'OMR') return value.toFixed(3) + ' ر.ع';
  if (currency === 'SAR') return value.toFixed(2) + ' ر.س';
  return value.toFixed(2) + ' ' + currency;
}

export function MenuPage(): JSX.Element {
  const { categories, isLoading, error } = useMenu();
  const [selected, setSelected] = useState<SelectedItem | null>(null);

  const cartCount = useCartStore((s) => s.lines.reduce((n, l) => n + l.quantity, 0));
  const openCart  = useCartStore((s) => s.open);

  return (
    <main className="min-h-screen bg-onyx-900 px-8 py-10">
      <TeaserIntro />
      <div className="mx-auto max-w-6xl">
        <header className="mb-10 flex items-baseline justify-between gap-4">
          <h1 className="aurum-gradient-text font-display text-5xl">قائمة الذواقة</h1>
          <div className="flex items-center gap-3">
            <button
              onClick={openCart}
              className="relative rounded-aurum-md border border-aurum-500/30 bg-onyx-700/60 px-4 py-2 text-sm text-aurum-200 hover:bg-aurum-500/10"
            >
              الطلب
              {cartCount > 0 ? (
                <span className="absolute -top-2 -left-2 grid h-6 w-6 place-items-center rounded-full bg-aurum-500 font-mono text-xs text-onyx-900">
                  {cartCount}
                </span>
              ) : null}
            </button>
            <Link to="/" className="text-sm text-aurum-300 hover:text-aurum-200">
              عودة
            </Link>
          </div>
        </header>

        <SommelierBar />

        {isLoading ? <MenuSkeleton /> : null}
        {error ? <MenuError message={error} /> : null}
        {!isLoading && !error && categories.length === 0 ? (
          <p className="text-center text-cream-100/60">لا توجد عناصر متاحة حالياً.</p>
        ) : null}

        {categories.map((category) => (
          <section key={category.id} className="mb-12">
            <h2 className="mb-4 font-display text-3xl text-aurum-300">{category.nameAr}</h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
              {category.items.map((item, index) => (
                <ItemCard
                  key={item.id}
                  item={item}
                  index={index}
                  onSelect={() => setSelected({ item, categorySlug: category.slug })}
                />
              ))}
            </div>
          </section>
        ))}
      </div>

      <CustomizerSheet
        item={selected?.item ?? null}
        categorySlug={selected?.categorySlug ?? ''}
        onClose={() => setSelected(null)}
      />
      <CartDrawer />
    </main>
  );
}

function ItemCard({
  item,
  index,
  onSelect,
}: {
  item: ApiMenuItem;
  index: number;
  onSelect: () => void;
}): JSX.Element {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
    >
      <button
        onClick={onSelect}
        className="block w-full text-right transition hover:scale-[1.01] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aurum-300"
      >
        <Card>
          {item.imageUrl ? (
            <div className="aspect-[4/3] overflow-hidden rounded-t-aurum-lg border-b border-aurum-500/15 bg-onyx-900">
              <img src={item.imageUrl} alt="" className="h-full w-full object-cover" loading="lazy" />
            </div>
          ) : null}
          <CardHeader>
            <CardTitle>{item.nameAr}</CardTitle>
            <p className="mt-1 font-body text-xs uppercase tracking-widest text-aurum-400">
              {item.nameEn}
            </p>
          </CardHeader>
          <CardBody>
            {item.descriptionAr ? (
              <p className="font-body text-sm leading-relaxed text-cream-100/90">
                {item.descriptionAr}
              </p>
            ) : null}
          </CardBody>
          <div className="flex items-center justify-between border-t border-aurum-500/15 px-6 py-4">
            <span className="font-display text-xl text-aurum-200">
              {formatPrice(item.basePrice, item.currency)}
            </span>
            {item.isSignature ? (
              <span className="rounded-full border border-aurum-500/40 px-2 py-0.5 text-xs text-aurum-300">
                مميّز
              </span>
            ) : null}
          </div>
        </Card>
      </button>
    </motion.div>
  );
}

function MenuSkeleton(): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="aurum-shimmer h-44 rounded-aurum-lg border border-aurum-500/10 bg-onyx-700/40"
          aria-hidden
        />
      ))}
    </div>
  );
}

function MenuError({ message }: { message: string }): JSX.Element {
  return (
    <div className="rounded-aurum-md border border-accent-ruby/40 bg-accent-ruby/10 p-6 text-center">
      <p className="font-display text-xl text-accent-ruby">تعذّر تحميل القائمة</p>
      <p className="mt-2 font-body text-sm text-cream-100/70">{message}</p>
      <p className="mt-4 font-body text-xs text-onyx-100">
        تأكّد من أن الـ API يعمل على المنفذ المحدد في .env.local
      </p>
    </div>
  );
}
